import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import htmlToText from 'html-to-text';
import concat from 'lodash/concat';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, Field } from 'formik';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import ReactSelect from 'components/form/Formik/FormikReactSelect';
import CheckboxGroup from 'components/form/Formik/FormikCheckboxGroup';
import Input from 'components/form/Formik/FormikInput';
import RichTextEditor from 'components/form/Formik/RichTextEditor';
import Button from 'components/common/Button';
import {
  GET_STUDENTS,
  CREATE_CONVO,
  GET_CONVOS,
} from 'graphql/conversations';
import { getFullName } from 'utils';
import useSnack from 'hooks/useSnack';
import Dropzone from "../../../../form/Dropzone";
import uuidv4 from "uuidv4";
import { Storage } from "aws-amplify";

const BUCKET = process.env.REACT_APP_BUCKET;

const useStyles = makeStyles((theme) => ({
  root: {
    width: 600,
    padding: theme.spacing(2),
  },
}));

const required = (value) => (!value ? 'This field is required' : null);

const NewConversation = ({ onClose, user, history }) => {
  const classes = useStyles();
  const snack = useSnack();
  const { data, loading: loadingStudents } = useQuery(GET_STUDENTS);
  const [createConvos] = useMutation(CREATE_CONVO);
  const [attachments, setAttachments] = useState([]);

  const onSubmit = async (values, { setSubmitting }) => {
    let payload = [];
    const files = attachments.length && await uploadFiles();
    const data = {
      type: 'email',
      created_by: user.id,
      student_id: values.student.user_id,
      subject: values.subject,
      messages: {
        data: [
          {
            type: 'email',
            html: values.message,
            text: htmlToText.fromString(values.message, {
              singleNewLineParagraphs: true,
            }),
            subject: values.subject,
            created_by: user.id,
            attachments: files,
          },
        ],
      },
      users: {
        data: [...values.recipients.map((i) => ({ user_id: i })), { user_id: user.id }],
      },
    };
    payload.push(data);

    return createConvos({
      variables: {
        data: payload,
      }
    }).then(
      (result) => {
        snack.success('Conversation created.');
        setSubmitting(false);
        history.push(`/inbox/email/${result.data.rows.returning[0].convo_id}`);
        onClose();
      },
      (e) => {
        snack.error(e.message);
        setSubmitting(false);
      },
    );
  };

  const uploadFiles = async () => {
    return await Promise.all(
      attachments.map((file) => {
        const key = uuidv4() + '-' + file.name;
        const contentType = file.type;

        return Storage.put(key, file, {
          contentType,
        }).then((result) => {
          const url = `https://${BUCKET}.s3.us-east-2.amazonaws.com/public/${result.key}`;
          return {
            url,
            name: file.name,
            size: file.size
          };
        });
      }),
    );
  };

  const handleUpload = (files) => {
    setAttachments(files);
  };

  const onRemove = (file) => {
    setAttachments(attachments.filter((a) => a.name !== file.name));
  };

  const students = data
    ? data.rows.map((i) => ({
      label: getFullName(i),
      value: i.user_id,
      ...i,
    }))
    : [];

  return (
    <div className={classes.root}>
      <Box
        marginBottom={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h6">Create new Message</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Formik initialValues={{}} onSubmit={onSubmit}>
        {({ values, handleSubmit, isSubmitting, isValid, dirty }) => {
          let recipientsHelp = '';
          const recipients = [];
          if (values.student) {
            recipients.push({
              label: `${values.student.label} - Student`,
              value: values.student.value,
            });
            values.student.guardians.forEach((g) => {
              let hasValue = false;
              if (g.guardian.user_email) {
                hasValue = true;
              }
              if (hasValue) {
                recipients.push({
                  label: getFullName(g.guardian) + ' - Guardian',
                  value: g.guardian.user_id,
                });
              } else {
                recipientsHelp =
                  'One or more guardians have no contact information';
              }
            });
            if (values.student.lc_user && user.id !== values.student.lc_user.user_id) {
              recipients.push({
                label: getFullName(values.student.lc_user) + ' - College Coach',
                value: values.student.lc_user.user_id,
              });
            }
            if (values.student.hc_user && user.id !== values.student.hc_user.user_id) {
              recipients.push({
                label:
                  getFullName(values.student.hc_user) + ' - Head Counselor',
                value: values.student.hc_user.user_id,
              });
            }
          }

          return (
            <>
              <Box marginBottom={2}>
                <Field
                  component={ReactSelect}
                  loading={loadingStudents}
                  isMulti={false}
                  name="student"
                  label="Select Student"
                  options={students}
                  validate={required}
                />
              </Box>
              <Box marginBottom={2}>
                {!values.student ? (
                  <Typography color="textSecondary" variant="subtitle2">
                    Please select a student
                  </Typography>
                ) : (
                  <Field
                    name="recipients"
                    label="Select recipients"
                    options={recipients}
                    component={CheckboxGroup}
                    helpText={recipientsHelp}
                    validate={required}
                  />
                )}
              </Box>
              <Box marginBottom={2}>
                <Field
                  fullWidth
                  name="subject"
                  component={Input}
                  validate={required}
                  label="Email subject"
                  placeholder="Enter Subject..."
                />
              </Box>
              <Box marginBottom={2}>
                <RichTextEditor
                  name="message"
                  autoFocus={false}
                  placeholder="Start your message..."
                  validate={required}
                />
                <Dropzone
                  multiple
                  accept=""
                  id="attachments"
                  onUpload={handleUpload}
                  onRemove={onRemove}
                />
              </Box>
              <Button
                loading={isSubmitting}
                disabled={!isValid || !dirty}
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default NewConversation;
